import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaPhoneAlt, FaEnvelope, FaFacebookF, FaInstagram } from 'react-icons/fa';
import { FaXTwitter, FaGoogle } from 'react-icons/fa6';
import redlogo from '../Asets/redlogo1.png';
import '../allcss/header.css';

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeLink, setActiveLink] = useState('');
  const location = useLocation();

  const toggleMenu = () => setMenuOpen(!menuOpen);
  const closeMenu = () => setMenuOpen(false);

  // 🔒 Lock page scroll while the mobile sidebar is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // ✅ Update active link based on route
  useEffect(() => {
    if (location.pathname === '/') {
      setActiveLink(location.hash ? `/${location.hash}` : '/');
    } else {
      setActiveLink(location.pathname); 
    }
  }, [location]);

  // ✅ Scroll tracking for sections (Home only)
  useEffect(() => {
    if (location.pathname === '/') {
      const sectionIds = ['about', 'programs', 'contact'];
      const handleScroll = () => {
        let foundActive = false;

        for (const id of sectionIds) {
          const section = document.getElementById(id);
          if (section) {
            const rect = section.getBoundingClientRect();
            if (rect.top <= 80 && rect.bottom > 80) {
              setActiveLink(`/#${id}`);
              foundActive = true;
              break;
            }
          }
        }

        if (!foundActive && window.scrollY < 300) {
          setActiveLink('/');
        }
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [location.pathname]);

  const handleLinkClick = (path) => {
    setActiveLink(path);
    closeMenu();
  };

  return (
    <header className="custom-header">
      <div className="header-top">
        <div className="logo-area">
          <Link 
            to="/" 
            onClick={() => { 
              window.scrollTo({ top: 0, behavior: 'smooth' }); 
              handleLinkClick('/'); 
            }}
          >
            <img src={redlogo} alt="School Logo" className="logo" />
          </Link>
        </div>

        <nav className="nav-area">
          <div className={`nav-links ${menuOpen ? 'hide-on-mobile' : ''}`}>
            <Link 
              to="/" 
              className={activeLink === '/' ? 'active' : ''} 
              onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); handleLinkClick('/'); }}
            >
              Home
            </Link>
            <Link 
              to="/#about" 
              className={activeLink === '/#about' ? 'active' : ''} 
              onClick={() => handleLinkClick('/#about')}
            >
              About Us
            </Link>
            <Link 
              to="/#programs" 
              className={activeLink === '/#programs' ? 'active' : ''} 
              onClick={() => handleLinkClick('/#programs')}
            >
              Programs
            </Link>
            <Link 
              to="/#contact" 
              className={activeLink === '/#contact' ? 'active' : ''} 
              onClick={() => handleLinkClick('/#contact')}
            >
              Admissions
            </Link>
            <Link 
              to="/blog" 
              className={activeLink === '/blog' ? 'active' : ''} 
              onClick={() => handleLinkClick('/blog')}
            >
              Blog
            </Link>
            {/* ✅ Added Art Culture Link */}
            <Link 
              to="/Artculture" 
              className={activeLink === '/Artculture' ? 'active' : ''} 
              onClick={() => handleLinkClick('/Artculture')}
            >
              Art Culture
            </Link>
            <Link 
              to="/contact" 
              className={activeLink === '/contact' ? 'active' : ''} 
              onClick={() => handleLinkClick('/contact')}
            >
              Contact Us
            </Link>
          </div>
        </nav>

        <div className="phone-section desktop-only">
          <FaPhoneAlt className="phone-icon" />
          <a href="tel:+917778851070" className="phone-number">
            +91 77788 51070
          </a>
        </div>

        <button 
          className={`menu-toggle ${menuOpen ? 'active' : ''}`} 
          onClick={toggleMenu} 
          aria-label="Toggle menu"
        >
          {menuOpen ? <FaTimes color="#FF0000" /> : <FaBars color="#FF0000" />}
        </button>
      </div>

      {/* 🔲 Backdrop overlay - click to close */}
      <div
        className={`sidebar-backdrop ${menuOpen ? 'show' : ''}`}
        onClick={closeMenu}
      />

      {/* 📱 Mobile Slide-in Sidebar */}
      <div className={`mobile-sidebar ${menuOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <img src={redlogo} alt="School Logo" className="sidebar-logo" />
          <button
            className="sidebar-close"
            onClick={closeMenu}
            aria-label="Close menu"
          >
            <FaTimes />
          </button>
        </div>

        <div className="nav-links">
          <Link 
            to="/" 
            className={activeLink === '/' ? 'active' : ''} 
            onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); handleLinkClick('/'); }}
          >
            Home
          </Link>
          <Link 
            to="/#about" 
            className={activeLink === '/#about' ? 'active' : ''} 
            onClick={() => handleLinkClick('/#about')}
          >
            About Us
          </Link>
          <Link 
            to="/#programs" 
            className={activeLink === '/#programs' ? 'active' : ''} 
            onClick={() => handleLinkClick('/#programs')}
          >
            Programs
          </Link>
          <Link 
            to="/#contact" 
            className={activeLink === '/#contact' ? 'active' : ''} 
            onClick={() => handleLinkClick('/#contact')}
          >
            Admissions
          </Link>
          <Link 
            to="/blog" 
            className={activeLink === '/blog' ? 'active' : ''} 
            onClick={() => handleLinkClick('/blog')}
          >
            Blog
          </Link>
          <Link 
            to="/Artculture" 
            className={activeLink === '/Artculture' ? 'active' : ''} 
            onClick={() => handleLinkClick('/Artculture')}
          >
            Art Culture
          </Link>
          <Link 
            to="/contact" 
            className={activeLink === '/contact' ? 'active' : ''} 
            onClick={() => handleLinkClick('/contact')}
          >
            Contact Us
          </Link>
        </div>

        <div className="sidebar-footer">
          <a href="tel:+917778851070" className="sidebar-phone">
            <FaPhoneAlt /> +91 77788 51070
          </a>
          <a href="mailto:redschoolhousebaroda@gmail.com" className="sidebar-email">
            <FaEnvelope /> redschoolhousebaroda@gmail.com
          </a>
          <div className="sidebar-socials">
            <a
              href="https://www.facebook.com/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
            >
              <FaFacebookF />
            </a>
            <a
              href="https://www.instagram.com/redschoolhousebaroda/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <FaInstagram />
            </a>
            <a
              href="https://x.com/RedSchoolHouse1"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="X (Twitter)"
            >
              <FaXTwitter />
            </a>
            <a
              href="https://share.google/wK4OG1iDD2OWbY0XK"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Google Business Profile"
            >
              <FaGoogle />
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;